import { PagePaths } from '#app/shared/navigation/pagePaths'
import { getBearerHeader, resetAuthToken } from '#app/shared/utils/auth'
import { datadogRum } from '@datadog/browser-rum'
import axios, { type AxiosError } from 'axios'

export const restClient = axios.create({
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFTOKEN'
})

restClient.interceptors.request.use(
  (config) => {
    if (!config || !config.headers) {
      return config
    }

    const header = getBearerHeader()

    if (!header) {
      return config
    }

    config.headers.Authorization = header

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * True when the visitor on this page is anonymous by design rather than
 * logged out: either they authenticate with a token in the URL (the
 * load-recommendations SMS flow) or they are part-way through the quickstart
 * signup, which has no account yet at all.
 *
 * Either way a 401 from an incidental session-only call the page happens to
 * make must not be read as "session expired" -- bouncing a carrier to sign-in
 * mid-signup would lose the number they had already proved.
 */
const isAnonymousPage = () =>
  Boolean(new URLSearchParams(window.location.search).get('token'))
  || window.location.pathname.startsWith(PagePaths.Carrier.Quickstart)

restClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      // no longer authenticated, redirect to sign-in with return URL
      datadogRum.addError(error)

      if (isAnonymousPage()) {
        // Leave the page as-is and let the caller handle its own failure.
        return Promise.reject(error)
      }

      // Reset auth token and redirect to sign-in with current path as return URL
      resetAuthToken()
      const currentPath = window.location.pathname

      // Prevent redirect loops by not setting returnTo if already on sign-in page
      const signInUrl = currentPath === PagePaths.SignIn
        ? PagePaths.SignIn
        : `${PagePaths.SignIn}?returnTo=${currentPath}`
      window.location.href = signInUrl

      return
    }
    if (error?.response?.data) {
      let data = error.response.data
      // Remove account related data from the logs
      if (error?.config?.url?.startsWith('/accounts')) {
        data = JSON.parse(JSON.stringify(data))
        if (data.form?.fields) {
          data.form.fields = {}
        }
      }
      // add the error to DataDog RUM
      datadogRum.addError(`${error} -> ${JSON.stringify(data)}`)
    } else {
      datadogRum.addError(error)
    }
    return Promise.reject(error)
  }
)

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const getErrorResponse = (error: any) => {
  if (!error) {
    return null
  }
  const { response } = error
  if (!error || !error.response) {
    return null
  }
  return response as AxiosError
}
